import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { NeuralBackground } from '@/components/hero/NeuralBackground';
import { SynapticText } from '@/components/hero/SynapticText';
import { Watch, Heart, Activity, Moon, Shield, Zap, CheckCircle, ArrowRight, Wifi, Clock, TrendingUp, Plus, Minus } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import { FloatingIslandCard } from '@/components/cards/FloatingIslandCard';

const Wearables = () => {
  const [openAccordion, setOpenAccordion] = useState<number | null>(0);
  
  const wearableFeatures = [
    {
      icon: Heart,
      title: "Continuous Heart Rate Monitoring",
      description: "Advanced sensors capture heart rate variability and rhythm patterns with clinical-grade accuracy throughout the day."
    },
    {
      icon: Activity,
      title: "Respiration & Movement Tracking",
      description: "Monitor respiratory patterns and physical activity levels to detect changes in patient condition and mobility."
    },
    {
      icon: Moon,
      title: "Sleep Pattern Analysis",
      description: "Track sleep quality, duration, and patterns to identify sleep disorders and recovery indicators."
    },
    {
      icon: Wifi,
      title: "Cloud-Connected Intelligence",
      description: "Real-time data synchronization delivers actionable clinical insights directly to your care team dashboard."
    },
    {
      icon: Shield,
      title: "FDA-Registered Reliability",
      description: "Medical-grade devices meet regulatory standards for clinical use and patient safety compliance."
    }
  ];

  const benefits = [
    {
      icon: Clock,
      title: "24/7 Continuous Monitoring",
      description: "Never miss critical changes with round-the-clock patient monitoring that captures vital signs and activity patterns."
    },
    {
      icon: TrendingUp,
      title: "Proactive Care Intervention",
      description: "Early warning systems alert care teams to potential issues before they become critical, enabling preventive care."
    },
    {
      icon: Heart,
      title: "Enhanced Patient Trust",
      description: "Discreet, comfortable wearables show patients you're always watching over their health and wellbeing."
    }
  ];


  return (
    <>
      <Helmet>
        <title>Wearables - FDA-Registered Patient Monitoring Devices | Canary</title>
        <meta name="description" content="Discreet, patient-centric wearable devices that effortlessly capture heart rate, respiration, movement, and sleep patterns—24/7. FDA-registered and cloud-connected." />
      </Helmet>

      <div className="min-h-screen" style={{ background: 'var(--gradient-depth)' }}>
        {/* Hero Section */}
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden" style={{ background: 'var(--gradient-earth)' }}>
          <NeuralBackground />
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-4xl mx-auto"
            >
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-6 py-3 mb-8">
                <Watch className="h-4 w-4 text-primary" />
                <span className="text-white text-sm font-medium">Wearables</span>
              </div>
              
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6">
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">
                  <SynapticText text="Discreet, patient-centric" delay={100} />
                </span>
                <span className="block text-white mt-2">
                  <SynapticText text="wearable devices" delay={800} />
                </span>
              </h1>
              
              <motion.p 
                className="text-xl text-gray-200 mb-8 max-w-3xl mx-auto leading-relaxed"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.5, duration: 0.8 }}
              >
                Effortlessly capture heart rate, respiration, movement, and sleep patterns—24/7. FDA-registered and cloud-connected, 
                they deliver actionable clinical insights and early warnings directly to your dashboard, empowering care teams to 
                intervene proactively and deepen patient trust.
              </motion.p>
              
              <motion.div 
                className="flex flex-col sm:flex-row gap-4 justify-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 2, duration: 0.8 }}
              >
                <RouterLink 
                  to="/contact-request-demo"
                  className="group bg-gradient-to-r from-primary to-accent text-white px-8 py-4 rounded-lg font-semibold
                           hover:shadow-xl hover:shadow-primary/25 transition-all duration-300 transform hover:-translate-y-1
                           flex items-center justify-center gap-2"
                >
                  Request Wearables Demo
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </RouterLink>
                
                <RouterLink 
                  to="/resources/case-studies"
                  className="group bg-white/10 backdrop-blur-sm border border-white/20 text-white px-8 py-4 rounded-lg font-semibold
                           hover:bg-white/20 transition-all duration-300 flex items-center justify-center gap-2"
                >
                  View Case Studies
                </RouterLink>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Key Features */}
        <section className="py-24" style={{ background: 'var(--gradient-warm)' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-white mb-6">
                Clinical-Grade Monitoring Technology
              </h2>
              <p className="text-xl text-gray-200 max-w-3xl mx-auto">
                Our FDA-registered wearable devices provide continuous health monitoring 
                with medical-grade accuracy and seamless clinical integration.
              </p>
            </motion.div>

            <div className="max-w-4xl mx-auto space-y-4">
              {wearableFeatures.map((feature, index) => {
                const Icon = feature.icon;
                const isOpen = openAccordion === index;
                
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="group"
                  >
                    <div className="bg-gradient-to-r from-primary/10 to-accent/10 backdrop-blur-lg border border-white/20 rounded-2xl overflow-hidden hover:from-primary/20 hover:to-accent/20 transition-all duration-300 shadow-lg">
                      <button
                        onClick={() => setOpenAccordion(isOpen ? null : index)}
                        className="w-full p-6 text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-primary/50 rounded-2xl"
                      >
                        <div className="flex items-center space-x-4">
                          <div className="flex-shrink-0 w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center">
                            <Icon className="w-6 h-6 text-primary" />
                          </div>
                          <div>
                            <h3 className="text-xl font-bold text-white group-hover:text-primary transition-colors">
                              {feature.title}
                            </h3>
                            <p className="text-gray-300 text-sm mt-1">
                              {isOpen ? "Click to collapse" : "Click to expand details"}
                            </p>
                          </div>
                        </div>
                        <motion.div
                          animate={{ rotate: isOpen ? 180 : 0 }}
                          transition={{ duration: 0.3 }}
                          className="flex-shrink-0"
                        >
                          <Plus className="w-6 h-6 text-gray-300" />
                        </motion.div>
                      </button>
                      
                      <AnimatePresence>
                        {isOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                            className="overflow-hidden"
                          >
                            <div className="px-6 pb-6">
                              <div className="border-t border-border pt-4">
                                <p className="text-gray-200 leading-relaxed text-lg">
                                  {feature.description}
                                </p>
                                
                                {/* Additional details for each feature */}
                                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                                  {index === 0 && (
                                    <>
                                      <div className="bg-muted/20 rounded-lg p-4">
                                        <h4 className="text-white font-semibold mb-2">Key Metrics</h4>
                                         <ul className="text-gray-200 text-sm space-y-1">
                                          <li>• Real-time BPM tracking</li>
                                          <li>• HRV analysis</li>
                                          <li>• Arrhythmia detection</li>
                                        </ul>
                                      </div>
                                      <div className="bg-muted/20 rounded-lg p-4">
                                        <h4 className="text-white font-semibold mb-2">Accuracy</h4>
                                         <ul className="text-gray-200 text-sm space-y-1">
                                          <li>• 99.5% clinical accuracy</li>
                                          <li>• FDA-cleared algorithms</li>
                                          <li>• Medical-grade sensors</li>
                                        </ul>
                                      </div>
                                    </>
                                  )}
                                  
                                  {index === 1 && (
                                    <>
                                      <div className="bg-muted/20 rounded-lg p-4">
                                        <h4 className="text-white font-semibold mb-2">Monitoring</h4>
                                         <ul className="text-gray-200 text-sm space-y-1">
                                           <li>• Respiratory rate</li>
                                           <li>• Activity levels</li>
                                           <li>• Movement patterns</li>
                                         </ul>
                                       </div>
                                       <div className="bg-muted/20 rounded-lg p-4">
                                         <h4 className="text-white font-semibold mb-2">Alerts</h4>
                                         <ul className="text-gray-200 text-sm space-y-1">
                                           <li>• Abnormal breathing</li>
                                           <li>• Inactivity warnings</li>
                                           <li>• Fall detection</li>
                                         </ul>
                                       </div>
                                     </>
                                   )}
                                   
                                   {index === 2 && (
                                     <>
                                       <div className="bg-muted/20 rounded-lg p-4">
                                         <h4 className="text-white font-semibold mb-2">Sleep Stages</h4>
                                         <ul className="text-gray-200 text-sm space-y-1">
                                           <li>• Deep sleep tracking</li>
                                           <li>• REM cycle analysis</li>
                                           <li>• Sleep efficiency</li>
                                         </ul>
                                       </div>
                                       <div className="bg-muted/20 rounded-lg p-4">
                                         <h4 className="text-white font-semibold mb-2">Insights</h4>
                                         <ul className="text-gray-200 text-sm space-y-1">
                                           <li>• Sleep quality scores</li>
                                           <li>• Recovery recommendations</li>
                                           <li>• Trend analysis</li>
                                         </ul>
                                       </div>
                                     </>
                                   )}
                                   
                                   {index === 3 && (
                                     <>
                                       <div className="bg-muted/20 rounded-lg p-4">
                                         <h4 className="text-white font-semibold mb-2">Connectivity</h4>
                                         <ul className="text-gray-200 text-sm space-y-1">
                                           <li>• Real-time sync</li>
                                           <li>• Cloud dashboard</li>
                                           <li>• Multi-device support</li>
                                         </ul>
                                       </div>
                                       <div className="bg-muted/20 rounded-lg p-4">
                                         <h4 className="text-white font-semibold mb-2">Integration</h4>
                                         <ul className="text-gray-200 text-sm space-y-1">
                                           <li>• EMR compatibility</li>
                                           <li>• API access</li>
                                           <li>• Care team alerts</li>
                                         </ul>
                                       </div>
                                     </>
                                   )}
                                   
                                   {index === 4 && (
                                     <>
                                       <div className="bg-muted/20 rounded-lg p-4">
                                         <h4 className="text-white font-semibold mb-2">Certifications</h4>
                                         <ul className="text-gray-200 text-sm space-y-1">
                                           <li>• FDA 510(k) cleared</li>
                                           <li>• CE marked</li>
                                           <li>• HIPAA compliant</li>
                                         </ul>
                                       </div>
                                       <div className="bg-muted/20 rounded-lg p-4">
                                         <h4 className="text-white font-semibold mb-2">Standards</h4>
                                         <ul className="text-gray-200 text-sm space-y-1">
                                          <li>• ISO 27001 security</li>
                                          <li>• Medical device class II</li>
                                          <li>• Clinical validation</li>
                                        </ul>
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-24" style={{ background: 'var(--gradient-earth)' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-white mb-6">
                Transform Patient Care with Continuous Monitoring
              </h2>
              <p className="text-xl text-gray-200 max-w-3xl mx-auto">
                Healthcare organizations using our wearable technology report significant 
                improvements in patient outcomes and care team efficiency.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => (
                <FloatingIslandCard
                  key={index}
                  icon={benefit.icon}
                  title={benefit.title}
                  description={benefit.description}
                  index={index}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-24" style={{ background: 'var(--gradient-warm)' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-white mb-6">
                Proven Results in Patient Monitoring
              </h2>
              <p className="text-xl text-gray-200 max-w-3xl mx-auto">
                Our wearable devices deliver measurable improvements in clinical outcomes and patient satisfaction.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.1 }}
                className="text-center p-8 rounded-xl bg-card backdrop-blur-sm border border-border"
              >
                <div className="text-4xl font-bold text-primary mb-2">85%</div>
                <div className="text-lg font-semibold text-black mb-2">Early Detection Rate</div>
                <p className="text-black">
                  Critical health events detected before symptoms appear
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-center p-8 rounded-xl bg-card backdrop-blur-sm border border-border"
              >
                <div className="text-4xl font-bold text-primary mb-2">30%</div>
                <div className="text-lg font-semibold text-black mb-2">Reduction in Hospital Readmissions</div>
                <p className="text-black">
                  Continuous monitoring enables proactive care intervention
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="text-center p-8 rounded-xl bg-card backdrop-blur-sm border border-border"
              >
                <div className="text-4xl font-bold text-primary mb-2">95%</div>
                <div className="text-lg font-semibold text-black mb-2">Patient Compliance Rate</div>
                <p className="text-black">
                  Comfortable, discreet design ensures consistent usage
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 border-t border-border/50" style={{ background: 'var(--gradient-earth)' }}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl font-bold text-white mb-6">
                Ready to Enhance Patient Care with Wearable Technology?
              </h2>
              <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
                Join leading healthcare organizations who have transformed patient monitoring 
                with our FDA-registered wearable devices.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <RouterLink 
                  to="/contact-request-demo"
                  className="group bg-gradient-to-r from-primary to-accent text-white px-8 py-4 rounded-lg font-semibold
                           hover:shadow-xl hover:shadow-primary/25 transition-all duration-300 transform hover:-translate-y-1
                           flex items-center justify-center gap-2"
                >
                  Schedule Wearables Consultation
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </RouterLink>
                
                <RouterLink 
                  to="/resources/white-papers-research"
                  className="group border-2 border-primary text-primary px-8 py-4 rounded-lg font-semibold
                           hover:bg-primary hover:text-white transition-all duration-300 flex items-center justify-center gap-2"
                >
                  Download Clinical Studies
                </RouterLink>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Wearables;